import React, { FC, useEffect, useState, useCallback, useRef } from "react";
import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faSearch } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

// Type definition for PDFDownloadLink render function
import moment from "moment";
import PaginationForm from "../../../../shared/components/molecules/commons/pagination-form";
Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/kanit/v1/L6VKvM17ZmevDynOiw7H9w.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/kanit/v1/wfLWkj1C4tYl7MoiFWS3bA.ttf",
});
interface PDFDownloadLinkRenderProps {
  blob: Blob | null;
  url: string | null;
  loading: boolean;
  error: Error | null;
}

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const thaiMonthsShort = [
  "ม.ค.",
  "ก.พ.",
  "มี.ค.",
  "เม.ย.",
  "พ.ค.",
  "มิ.ย.",
  "ก.ค.",
  "ส.ค.",
  "ก.ย.",
  "ต.ค.",
  "พ.ย.",
  "ธ.ค.",
];

const ReportRetirementTrendListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
    year: moment().year() + 543,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };
  const [displayYear, setDisplayYear] = useState(moment().year() + 543);

  const headers: Header[] = [
    {
      label: "ลำดับ",
      field: "index",
      order: null,
      isOrder: true,
    },
    {
      label: "ชื่อ-นามสกุล",
      field: "name",
      order: null,
      isOrder: true,
    },
    {
      label: "รหัสพนักงาน",
      field: "code",
      order: null,
      isOrder: true,
    },
    {
      label: "ว/ด/ป เกิด",
      field: "birth_date",
      order: null,
      isOrder: true,
    },
    {
      label: "ตำแหน่ง",
      field: "position",
      order: null,
      isOrder: true,
    },
    {
      label: "ระดับ",
      field: "position_level",
      order: null,
      isOrder: true,
    },
    {
      label: "ว/ด/ป บรรจุ",
      field: "recruitment_date",
      order: null,
      isOrder: true,
    },
    {
      label: "สังกัด",
      field: "department",
      order: null,
      isOrder: true,
    },
    {
      label: "เงินเดือน",
      field: "salary",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result = await HrOnlineService.getReportRetirementTrendList(
          params
        );
        const resultAll = await HrOnlineService.getReportRetirementTrendList({
          ...params,
          page_size: 10000,
          page: 1,
        });
        setData(result);
        setDataExport(resultAll);
        setDisplayYear(params.year);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering, queryParams.page_size, queryParams.page]
  );

  const tableRowNoData = () => {
    if (data.rows?.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 20, fontFamily: "kanit-regular" }}>
        <View>
          {/* หัวรายงาน */}
          <View style={{ marginBottom: 20 }}>
            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 3,
              }}
            >
              องค์การตลาดเพื่อเกษตรกร
            </Text>

            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 3,
              }}
            >
              รายงานผู้เกษียณอายุในปี {displayYear}
            </Text>

            <Text
              style={{
                fontSize: 13,
                textAlign: "center",
                marginBottom: 10,
              }}
            >
              ประจำวันที่{" "}
              {moment().format("D") +
                " " +
                thaiMonths[moment().month()] +
                " " +
                (moment().year() + 543)}
            </Text>
          </View>

          <View style={{ marginBottom: 10 }} fixed>
            {/* หัวตาราง */}
            <View
              style={{
                flexDirection: "row",
                padding: 5,
                borderBottomWidth: 1,
                borderTopWidth: 1,
              }}
            >
              <Text
                style={{
                  width: "7%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ลำดับที่
              </Text>
              <Text
                style={{
                  width: "18%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ชื่อ-นามสกุล
              </Text>
              <Text
                style={{
                  width: "9%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                รหัสพนักงาน
              </Text>
              <Text
                style={{
                  width: "8%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ว/ด/ป เกิด
              </Text>
              <Text
                style={{
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ตำแหน่งง
              </Text>
              <Text
                style={{
                  width: "5%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ระดับ
              </Text>
              <Text
                style={{
                  width: "10%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                ว/ด/ป บรรจุ
              </Text>
              <Text
                style={{
                  width: "15%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                สังกัด
              </Text>
              <Text
                style={{
                  width: "8%",
                  fontSize: 10,
                  textAlign: "center",
                }}
              >
                เงินเดือน
              </Text>
            </View>

            {/* ข้อมูลพนักงาน */}
            {data.rows?.map((item: any, index: any) => (
              <View
                key={index}
                style={{
                  flexDirection: "row",
                  padding: 3,
                }}
              >
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "7%",
                    fontSize: 8,
                    textAlign: "center",
                  }}
                >
                  {index + 1}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "18%",
                    fontSize: 8,
                    textAlign: "left",
                    paddingLeft: 1,
                  }}
                >
                  {item.name}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "9%",
                    fontSize: 8,
                    textAlign: "left",
                  }}
                >
                  {item.code}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "12%",
                    fontSize: 8,
                    textAlign: "left",
                  }}
                >
                  {item.birthday
                    ? moment(item.birthday).format("DD") +
                      " " +
                      thaiMonthsShort[moment(item.birthday).month()] +
                      " " +
                      (moment(item.birthday).year() + 543)
                    : "-"}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 8,
                    textAlign: "left",
                    paddingLeft: 1,
                  }}
                >
                  {item.position_name}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "5%",
                    fontSize: 8,
                    textAlign: "left",
                  }}
                >
                  {item.position_level}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "10%",
                    fontSize: 8,
                    textAlign: "left",
                  }}
                >
                  {item.tenure_position_date_start
                    ? moment(item.tenure_position_date_start).format("DD") +
                      " " +
                      thaiMonthsShort[
                        moment(item.tenure_position_date_start).month()
                      ] +
                      " " +
                      (moment(item.tenure_position_date_start).year() + 543)
                    : "-"}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "15%",
                    fontSize: 8,
                    textAlign: "left",
                    paddingLeft: 1,
                  }}
                >
                  {item.department_offices_name
                    ? item.department_offices_name
                    : item.division_group_office_name
                    ? item.division_group_office_name
                    : item.structure_departments_name
                    ? item.structure_departments_name
                    : "-"}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "8%",
                    fontSize: 8,
                    textAlign: "right",
                    paddingRight: 1,
                  }}
                >
                  {item.salary ? item.salary.toLocaleString() : "-"}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </Page>
    </Document>
  );

  const handleYearChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQueryParams({
      ...queryParams,
      year: e.target.value,
    });
  };

  const onChangePageSize = (page_size: number) => {
    setQueryParams({
      ...queryParams,
      page_size: page_size,
    });
  };

  const onChangePage = (page: number) => {
    setQueryParams({
      ...queryParams,
      page: page,
    });
  };
  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">
          รายงานผู้เกษียณอายุในปี {displayYear}
        </h2>
      </div>
      {loaderComponent}
      <div className="row">
        <label className="form-label">ปี</label>
        <div className="col-sm-12 col-md-2">
          <input
            className="form-control"
            type="text"
            value={queryParams.year ? queryParams.year : ""}
            onChange={handleYearChange}
            name="search"
          />
        </div>
        <div className="col-sm-12 col-md-4">
          <Button
            variant="primary"
            type="button"
            className="d-inline-block px-4"
            onClick={() => fetchData(queryParams)}
          >
            <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
          </Button>
        </div>
        <div className="col-sm-12 col-md-6 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานผู้เกษียณอายุในปี_${queryParams.year}.pdf`}
          >
            {(({ loading }: any) => (
              <Button
                variant="primary"
                className="btn-action btn-mof-primary me-2"
                disabled={loading}
              >
                <FontAwesomeIcon icon={faFileExport} className="me-2" />
                {loading ? "Loading..." : "Export"}
              </Button>
            )) as any}
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.map((data: any, index: any) => {
              return (
                <ReportEmployeeCountByTypeRow
                  key={index}
                  {...data}
                  index={index + 1}
                />
              );
            })}
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
      <div className="">
        <PaginationForm
          current_page={queryParams.page}
          count={data.count}
          page_size={queryParams.page_size}
          onChangePageSize={onChangePageSize}
          onChangePage={onChangePage}
        />
      </div>
    </>
  );
};

const ReportEmployeeCountByTypeRow: FC<any> = ({
  index,
  name,
  code,
  birthday,
  position_name,
  position_level,
  tenure_position_date_start,
  department_offices_name,
  division_group_office_name,
  structure_departments_name,
  salary,
}) => {
  return (
    <tr>
      <td>{index}</td>
      <td>{name}</td>
      <td>{code}</td>
      <td>
        {birthday
          ? moment(birthday).format("DD") +
            " " +
            thaiMonthsShort[moment(birthday).month()] +
            " " +
            (moment(birthday).year() + 543)
          : "-"}
      </td>
      <td>{position_name}</td>
      <td>{position_level}</td>
      <td>
        {tenure_position_date_start
          ? moment(tenure_position_date_start).format("DD") +
            " " +
            thaiMonthsShort[moment(tenure_position_date_start).month()] +
            " " +
            (moment(tenure_position_date_start).year() + 543)
          : "-"}
      </td>
      <td>
        {department_offices_name
          ? department_offices_name
          : division_group_office_name
          ? division_group_office_name
          : structure_departments_name
          ? structure_departments_name
          : "-"}
      </td>
      <td>{salary ? salary.toLocaleString() : "-"}</td>
    </tr>
  );
};

export default ReportRetirementTrendListPage;
